/**
 * The four things a brush can do to a grid of terrain.
 *
 * Pure, and taking the grid's size as an argument rather than closing over a world, because the same
 * four operations serve two very different surfaces: an admin drawing a curriculum island and a
 * member arranging their own. Nothing in here knows which.
 *
 * Every function returns a **new** array, or null when nothing changed. Null is not a nicety — the
 * caller uses it to avoid pushing an undo entry for a stroke that painted grass onto grass, which is
 * how a drag across already-correct ground ends up costing four presses of Poništi.
 */

import { MAX_ISLAND_SIZE, Terrain } from '../../../core/terrain';
import type { World } from '../../game/world';

export interface Field {
  width: number;
  height: number;
}

/**
 * Paint from one tile to another, everything in between included.
 *
 * The interpolation is not a nicety. Pointer events arrive every frame at best and a great deal less
 * often on a trackpad or over a slow render, so a quick stroke across an island delivers maybe six
 * positions for forty tiles. Painting only where the events landed leaves a dotted line, and the
 * person drawing it concludes the brush is broken — which, for their purposes, it is.
 */
export function paintLine(
  field: Field,
  from: { x: number; y: number },
  to: { x: number; y: number },
  value: Terrain,
  brush: number,
  source: ArrayLike<number>,
): Uint8Array | null {
  const half = Math.floor((brush - 1) / 2);
  const next = new Uint8Array(source);
  let touched = false;

  const stamp = (cx: number, cy: number) => {
    for (let dy = -half; dy <= brush - 1 - half; dy++) {
      for (let dx = -half; dx <= brush - 1 - half; dx++) {
        const nx = cx + dx;
        const ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= field.width || ny >= field.height) continue;
        const index = ny * field.width + nx;
        if (next[index] === value) continue;
        next[index] = value;
        touched = true;
      }
    }
  };

  // Bresenham, so a diagonal stroke leaves a connected line rather than a staircase of gaps.
  let x = from.x;
  let y = from.y;
  const dx = Math.abs(to.x - x);
  const dy = -Math.abs(to.y - y);
  const stepX = x < to.x ? 1 : -1;
  const stepY = y < to.y ? 1 : -1;
  let error = dx + dy;
  // Bounded rather than trusted: the loop's exit depends on two coordinates meeting, and a
  // coordinate that arrives out of range would otherwise spin forever.
  for (let guard = 0; guard <= MAX_ISLAND_SIZE * 2; guard++) {
    stamp(x, y);
    if (x === to.x && y === to.y) break;
    const doubled = error * 2;
    if (doubled >= dy) {
      error += dy;
      x += stepX;
    }
    if (doubled <= dx) {
      error += dx;
      y += stepY;
    }
  }

  return touched ? next : null;
}

/** Bucket fill over the connected run of whatever was clicked. Four-way, like the walk. */
export function fillFrom(
  field: Field,
  x: number,
  y: number,
  value: Terrain,
  source: ArrayLike<number>,
): Uint8Array | null {
  const from = source[y * field.width + x];
  if (from === undefined || from === value) return null;
  const next = new Uint8Array(source);
  const queue = [y * field.width + x];
  next[queue[0]!] = value;
  for (let head = 0; head < queue.length; head++) {
    const index = queue[head]!;
    const cx = index % field.width;
    const cy = (index - cx) / field.width;
    for (const [dx, dy] of [
      [0, -1],
      [0, 1],
      [-1, 0],
      [1, 0],
    ] as const) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= field.width || ny >= field.height) continue;
      const at = ny * field.width + nx;
      if (next[at] !== from) continue;
      next[at] = value;
      queue.push(at);
    }
  }
  return next;
}

export function rectBetween(
  field: Field,
  a: { x: number; y: number },
  b: { x: number; y: number },
  value: Terrain,
  source: ArrayLike<number>,
): Uint8Array {
  const next = new Uint8Array(source);
  for (let y = Math.min(a.y, b.y); y <= Math.max(a.y, b.y); y++)
    for (let x = Math.min(a.x, b.x); x <= Math.max(a.x, b.x); x++)
      next[y * field.width + x] = value;
  return next;
}

/**
 * A short, stable fingerprint of a grid.
 *
 * Cheap enough to compute on every render and specific enough that any edit changes it, which is
 * what lets the expensive ground layer be cached against it rather than repainted for every hover.
 */
export function fingerprint(tiles: ArrayLike<number>): string {
  let sum = 2166136261;
  for (let index = 0; index < tiles.length; index++) {
    sum ^= tiles[index]!;
    sum = Math.imul(sum, 16777619);
  }
  return (sum >>> 0).toString(36);
}

/**
 * A World with nothing in it but ground, which is all `renderGroundLayer` reads.
 *
 * Here rather than in the renderer because both the canvas and the palette swatches need one, and
 * neither of them has an archipelago to hand.
 */
export function previewWorld(tiles: Uint8Array, width: number, height: number): World {
  return {
    width,
    height,
    tiles,
    markers: [],
    islands: [],
    characters: [],
    props: [],
    origin: { x: 0, y: 0 },
    signature: '',
  };
}
